import React, { useState, useRef, useEffect } from "react";
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { getEngineeringVendorById } from "../../../Service/Engineeringservicefil";
import { useLoader } from "../../../context/LoaderProvider";

const EngineeringLocation = ({ formData, setFormData, onNext, onBack }) => {
    const [openMap, setOpenMap] = useState(false);
    const [center, setCenter] = useState({ lat: 17.385, lng: 78.486 });
    const [marker, setMarker] = useState(null);
    const autoRef = useRef(null);
    const [zoom, setZoom] = useState(13);
    const [loading, setLoading] = useState(false);
    const { showLoader, hideLoader } = useLoader();

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "AIzaSyCf7bYn7iDIs2T6-sDnmr7qWy9oFZOPOuc",
        libraries: ["places"]
    });

    useEffect(() => {
        const fetchVendorData = async () => {
            try {
                showLoader();
                const vendorId = localStorage.getItem("vendorId");
                if (!vendorId) return;
                
                const data = await getEngineeringVendorById(vendorId);
                
                if (data && Object.keys(data).length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        latitude: data.latitude || "",
                        longitude: data.longitude || "",
                        doorNumber: data.doorNumber || "",
                        addressLine: data.addressLine || "",
                        landMark: data.landMark || "",
                        city: data.city || "",
                        state: data.state || "",
                        pincode: data.pincode || "",
                        fullAddress: data.fullAddress || ""
                    }));
                    
                    if (data.latitude && data.longitude) {
                        const lat = parseFloat(data.latitude);
                        const lng = parseFloat(data.longitude);
                        setCenter({ lat, lng });
                        setMarker({ lat, lng });
                    }
                }
            } catch (error) {
                console.error("Error fetching location data:", error);
            } finally {
                hideLoader();
            }
        };
        fetchVendorData();
    }, []);

    const handleCurrentLocation = () => {
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            setCenter({ lat, lng });
            setMarker({ lat, lng });

            try {
                const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCf7bYn7iDIs2T6-sDnmr7qWy9oFZOPOuc`);
                const data = await res.json();
                const result = data.results[0];
                const getComponent = (type) => result.address_components.find(c => c.types.includes(type))?.long_name || "";
                const city = getComponent("locality");
                const state = getComponent("administrative_area_level_1");
                const pincode = getComponent("postal_code");
                const addressParts = result.formatted_address.split(",");

                setFormData(prev => ({
                    ...prev,
                    latitude: lat,
                    longitude: lng,
                    fullAddress: result.formatted_address,
                    doorNumber: addressParts[0] || "",
                    addressLine: addressParts.slice(1, 3).join(","),
                    landMark: addressParts[2] || "",
                    city,
                    state,
                    pincode
                }));
            } catch (err) {
                console.error(err);
            }
        });
    };

    const onPlaceChanged = () => {
        const place = autoRef.current.getPlace();
        if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setCenter({ lat, lng });
            setMarker({ lat, lng });
            setFormData({ ...formData, latitude: lat, longitude: lng, fullAddress: place.formatted_address });
        }
    };

    return (
        <div style={cardStyle}>
            <h3 style={sectionTitle}>Location Details</h3>

            {/* Row 1: Door No + Street/Address */}
            <div style={rowStyle}>
                <div style={field}>
                    <label style={label}>Door No</label>
                    <input style={input} value={formData.doorNumber || ""} onChange={(e) => setFormData({ ...formData, doorNumber: e.target.value })} placeholder="Enter door number" />
                </div>
                <div style={field}>
                    <label style={label}>Street / Address</label>
                    <input style={input} value={formData.addressLine || ""} onChange={(e) => setFormData({ ...formData, addressLine: e.target.value })} placeholder="Enter street address" />
                </div>
            </div>

            {/* Row 2: Landmark + Pincode */}
            <div style={rowStyle}>
                <div style={field}>
                    <label style={label}>Landmark</label>
                    <input style={input} value={formData.landMark || ""} onChange={(e) => setFormData({ ...formData, landMark: e.target.value })} placeholder="Enter landmark" />
                </div>
                <div style={field}>
                    <label style={label}>Pincode</label>
                    <input style={input} value={formData.pincode || ""} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} placeholder="Enter pincode" />
                </div>
            </div>

            {/* Row 3: City + State */}
            <div style={rowStyle}>
                <div style={field}>
                    <label style={label}>City</label>
                    <input style={input} value={formData.city || ""} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Enter city" />
                </div>
                <div style={field}>
                    <label style={label}>State</label>
                    <input style={input} value={formData.state || ""} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="Enter state" />
                </div>
            </div>

            {/* Row 4: Latitude + Longitude */}
            <div style={rowStyle}>
                <div style={field}>
                    <label style={label}>Latitude</label>
                    <input style={input} value={formData.latitude || ""} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} placeholder="Enter latitude" />
                </div>
                <div style={field}>
                    <label style={label}>Longitude</label>
                    <input style={input} value={formData.longitude || ""} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} placeholder="Enter longitude" />
                </div>
            </div>

            {/* Row 5: Location Picker Button */}
            <div style={fieldFull}>
                <label style={label}>Location *</label>
                <button onClick={() => setOpenMap(true)} style={locationBtn}>📍 Add Location</button>
            </div>

            {/* Buttons */}
            <div style={buttonContainer}>
                <button style={backBtn} onClick={onBack}>← Back</button>
                <button style={nextBtn} onClick={onNext}>Save & Next →</button>
            </div>

            {/* Map Modal */}
            {openMap && (
                <div style={overlayStyle} onClick={() => setOpenMap(false)}>
                    <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                        <h3>Select Location</h3>
                        {!isLoaded ? <p>Loading map...</p> : (
                            <>
                                <Autocomplete onLoad={(ref) => (autoRef.current = ref)} onPlaceChanged={onPlaceChanged}>
                                    <input type="text" placeholder="Search cities or areas..." style={searchInput} />
                                </Autocomplete>
                                <div style={{ position: "relative" }}>
                                    <GoogleMap mapContainerStyle={{ width: "100%", height: "350px" }} center={center} zoom={zoom} options={{ disableDefaultUI: true, zoomControl: false }} onClick={async (e) => {
                                        const lat = e.latLng.lat();
                                        const lng = e.latLng.lng();
                                        setMarker({ lat, lng });
                                        try {
                                            const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCf7bYn7iDIs2T6-sDnmr7qWy9oFZOPOuc`);
                                            const data = await res.json();
                                            const result = data.results[0];
                                            const getComponent = (type) => result.address_components.find(c => c.types.includes(type))?.long_name || "";
                                            const city = getComponent("locality") || getComponent("administrative_area_level_2");
                                            const state = getComponent("administrative_area_level_1");
                                            const pincode = getComponent("postal_code");
                                            const landmark = result.address_components[1]?.long_name || "";
                                            const addressParts = result.formatted_address.split(",");
                                            setFormData(prev => ({ ...prev, latitude: lat, longitude: lng, fullAddress: result.formatted_address, doorNumber: addressParts[0] || "", addressLine: addressParts.slice(1, 3).join(",") || "", landMark: landmark, city, state, pincode }));
                                        } catch (err) { console.error(err); }
                                    }}>
                                        {marker && <Marker position={marker} />}
                                    </GoogleMap>
                                    <div style={mapControls}>
                                        <div style={controlBtn} onClick={() => setZoom(z => z + 1)}>+</div>
                                        <div style={controlBtn} onClick={() => setZoom(z => z - 1)}>−</div>
                                        <div style={controlBtn} onClick={handleCurrentLocation}><MyLocationIcon style={{ color: "#1a73e8", fontSize: "22px" }} /></div>
                                    </div>
                                </div>
                            </>
                        )}
                        <div style={{ textAlign: "center", marginTop: "10px" }}>
                            <button onClick={() => setOpenMap(false)} style={selectBtn}>Save Location</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EngineeringLocation;

const cardStyle = { background: "#fff", borderRadius: "12px" };
const sectionTitle = { fontSize: "16px", fontWeight: "600", marginBottom: "20px", color: "#111827" };
const rowStyle = { display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "18px" };
const field = { flex: 1, minWidth: "200px" };
const fieldFull = { width: "100%", marginBottom: "18px" };
const label = { fontSize: "13px", fontWeight: "600", marginBottom: "6px", display: "block", color: "#374151" };
const input = { width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#f9fafb", fontSize: "13px", boxSizing: "border-box" };
const locationBtn = { width: "100%", height: "40px", background: "#2563eb", color: "#fff", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "500", fontSize: "14px" };
const buttonContainer = { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "30px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" };
const backBtn = { background: "#6b7280", color: "#fff", padding: "10px 24px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "500", fontSize: "14px" };
const nextBtn = { background: "#f97316", color: "#fff", padding: "10px 24px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "500", fontSize: "14px" };
const overlayStyle = { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 };
const modalStyle = { width: "500px", background: "#fff", padding: "20px", borderRadius: "12px" };
const selectBtn = { background: "#10b981", color: "#fff", padding: "8px 20px", borderRadius: "8px", border: "none", cursor: "pointer" };
const searchInput = { width: "100%", padding: "10px", borderRadius: "20px", border: "1px solid #ccc", marginBottom: "10px" };
const mapControls = { position: "absolute", right: "10px", bottom: "80px", display: "flex", flexDirection: "column", gap: "10px", zIndex: 10 };
const controlBtn = { width: "42px", height: "42px", background: "#fff", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "18px", boxShadow: "0 2px 6px rgba(0,0,0,0.3)" };